"use client";

import React, { useState, useMemo } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowRight, Funnel, ShieldCheck, Lock, BookOpen } from "lucide-react";
const INDUSTRIES = [{
  id: 10,
  label: "FMCG"
}, {
  id: 11,
  label: "Natural Wellness"
}, {
  id: 12,
  label: "Sustainability"
}, {
  id: 13,
  label: "Agriculture"
}, {
  id: 14,
  label: "D2C"
}];
const CLASS_DESCRIPTIONS = [{
  id: 1,
  classNumber: "01",
  type: "Goods",
  description: "Chemicals for industry, science and photography, as well as in agriculture, horticulture and forestry."
}, {
  id: 2,
  classNumber: "02",
  type: "Goods",
  description: "Paints, varnishes, lacquers; preservatives against rust and against deterioration of wood; colorants."
}, {
  id: 3,
  classNumber: "03",
  type: "Goods",
  description: "Non-medicated cosmetics and toiletry preparations; non-medicated dentifrices; perfumery, essential oils."
}, {
  id: 4,
  classNumber: "04",
  type: "Goods",
  description: "Industrial oils and greases, wax; lubricants; dust absorbing, wetting and binding compositions; fuels and illuminants."
}, {
  id: 5,
  classNumber: "05",
  type: "Goods",
  description: "Pharmaceuticals, medical and veterinary preparations; sanitary preparations for medical purposes; dietetic food."
}, {
  id: 6,
  classNumber: "06",
  type: "Goods",
  description: "Common metals and their alloys, ores; metal materials for building and construction; transportable buildings of metal."
}, {
  id: 7,
  classNumber: "07",
  type: "Goods",
  description: "Machines, machine tools, power-operated tools; motors and engines; machine coupling and transmission components."
}, {
  id: 8,
  classNumber: "08",
  type: "Goods",
  description: "Hand tools and implements, hand-operated; cutlery; side arms, except firearms; razors."
}, {
  id: 9,
  classNumber: "09",
  type: "Goods",
  description: "Scientific, research, navigation, surveying, photographic, cinematographic, audiovisual, optical, weighing, measuring."
}, {
  id: 10,
  classNumber: "10",
  type: "Goods",
  description: "Surgical, medical, dental and veterinary apparatus and instruments; artificial limbs, eyes and teeth; orthopedic articles."
}, {
  id: 11,
  classNumber: "11",
  type: "Goods",
  description: "Apparatus and installations for lighting, heating, cooling, steam generating, cooking, drying, ventilating, water supply."
}, {
  id: 12,
  classNumber: "12",
  type: "Goods",
  description: "Vehicles; apparatus for locomotion by land, air or water."
}, {
  id: 13,
  classNumber: "13",
  type: "Goods",
  description: "Firearms; ammunition and projectiles; explosives; fireworks."
}, {
  id: 14,
  classNumber: "14",
  type: "Goods",
  description: "Precious metals and their alloys; jewellery, precious and semi-precious stones; horological and chronometric instruments."
}, {
  id: 15,
  classNumber: "15",
  type: "Goods",
  description: "Musical instruments; music stands and stands for musical instruments; conductors' batons."
}, {
  id: 16,
  classNumber: "16",
  type: "Goods",
  description: "Paper and cardboard; printed matter; bookbinding material; photographs; stationery and office requisites."
}, {
  id: 17,
  classNumber: "17",
  type: "Goods",
  description: "Unprocessed and semi-processed rubber, gutta-percha, gum, asbestos, mica and substitutes for all these materials."
}, {
  id: 18,
  classNumber: "18",
  type: "Goods",
  description: "Leather and imitations of leather; animal skins and hides; luggage and carrying bags; umbrellas and parasols."
}, {
  id: 19,
  classNumber: "19",
  type: "Goods",
  description: "Materials, not of metal, for building and construction; rigid pipes, not of metal, for building; asphalt, pitch, tar."
}, {
  id: 20,
  classNumber: "20",
  type: "Goods",
  description: "Furniture, mirrors, picture frames; containers, not of metal, for storage or transport; unworked or semi-worked bone."
}, {
  id: 21,
  classNumber: "21",
  type: "Goods",
  description: "Household or kitchen utensils and containers; cookware and tableware, except forks, knives and spoons; combs and sponges."
}, {
  id: 22,
  classNumber: "22",
  type: "Goods",
  description: "Ropes and string; nets; tents and tarpaulins; awnings of textile or synthetic materials; sails; sacks for the transport."
}, {
  id: 23,
  classNumber: "23",
  type: "Goods",
  description: "Yarns and threads, for textile use."
}, {
  id: 24,
  classNumber: "24",
  type: "Goods",
  description: "Textiles and substitutes for textiles; household linen; curtains of textile or plastic."
}, {
  id: 25,
  classNumber: "25",
  type: "Goods",
  description: "Clothing, footwear, headwear."
}, {
  id: 26,
  classNumber: "26",
  type: "Goods",
  description: "Lace, braid and embroidery, and haberdashery ribbons and bows; buttons, hooks and eyes, pins and needles."
}, {
  id: 27,
  classNumber: "27",
  type: "Goods",
  description: "Carpets, rugs, mats and matting, linoleum and other materials for covering existing floors; wall hangings, not of textile."
}, {
  id: 28,
  classNumber: "28",
  type: "Goods",
  description: "Games, toys and playthings; video game apparatus; gymnastic and sporting articles; decorations for Christmas trees."
}, {
  id: 29,
  classNumber: "29",
  type: "Goods",
  description: "Meat, fish, poultry and game; meat extracts; preserved, frozen, dried and cooked fruits and vegetables; jellies, jams."
}, {
  id: 30,
  classNumber: "30",
  type: "Goods",
  description: "Coffee, tea, cocoa and artificial coffee; rice, pasta and noodles; tapioca and sago; flour and preparations made from cereals."
}, {
  id: 31,
  classNumber: "31",
  type: "Goods",
  description: "Raw and unprocessed agricultural, aquacultural, horticultural and forestry products; raw and unprocessed grains and seeds."
}, {
  id: 32,
  classNumber: "32",
  type: "Goods",
  description: "Beers; non-alcoholic beverages; mineral and aerated waters; fruit beverages and fruit juices; syrups."
}, {
  id: 33,
  classNumber: "33",
  type: "Goods",
  description: "Alcoholic beverages, except beers; alcoholic preparations for making beverages."
}, {
  id: 34,
  classNumber: "34",
  type: "Goods",
  description: "Tobacco and tobacco substitutes; cigarettes and cigars; electronic cigarettes and oral vaporizers for smokers."
}, {
  id: 35,
  classNumber: "35",
  type: "Services",
  description: "Advertising; business management, organization and administration; office functions."
}, {
  id: 36,
  classNumber: "36",
  type: "Services",
  description: "Financial, monetary and banking services; insurance services; real estate affairs."
}, {
  id: 37,
  classNumber: "37",
  type: "Services",
  description: "Construction services; installation and repair services; mining extraction, oil and gas drilling."
}, {
  id: 38,
  classNumber: "38",
  type: "Services",
  description: "Telecommunications services."
}, {
  id: 39,
  classNumber: "39",
  type: "Services",
  description: "Transport; packaging and storage of goods; travel arrangement."
}, {
  id: 40,
  classNumber: "40",
  type: "Services",
  description: "Treatment of materials; recycling of waste and trash; air purification and treatment of water; printing services."
}, {
  id: 41,
  classNumber: "41",
  type: "Services",
  description: "Education; providing of training; entertainment; sporting and cultural activities."
}, {
  id: 42,
  classNumber: "42",
  type: "Services",
  description: "Scientific and technological services and research and design relating thereto; industrial analysis, research and design."
}, {
  id: 43,
  classNumber: "43",
  type: "Services",
  description: "Services for providing food and drink; temporary accommodation."
}, {
  id: 44,
  classNumber: "44",
  type: "Services",
  description: "Medical services; veterinary services; hygienic and beauty care for human beings or animals; agriculture, aquaculture."
}, {
  id: 45,
  classNumber: "45",
  type: "Services",
  description: "Legal services; security services for the physical protection of tangible property and individuals; personal and social services."
}];
function PortfolioContent() {
  const {
    posts,
    loading,
    error,
    setQuery,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  const [selectedIndustries, setSelectedIndustries] = useState<number[]>([]);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const displayedClasses = useMemo(() => {
    return CLASS_DESCRIPTIONS;
  }, []);
  const handleIndustryChange = (id: number, checked: boolean) => {
    const newSelection = checked ? [...selectedIndustries, id] : selectedIndustries.filter(i => i !== id);
    setSelectedIndustries(newSelection);
    setQuery(prev => ({
      ...prev,
      categories: newSelection.length > 0 ? newSelection : undefined
    }));
  };
  return <div className="container mx-auto px-4">
      <div className="mb-12 text-center md:text-left">
        <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-4">
          Our Catalogue
        </h2>
        <p className="text-lg text-muted-foreground max-w-2xl">
          A curated portfolio of registered trademarks across high-growth industries. Skip the 6–12 month wait and acquire a legally-vetted brand today.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:hidden mb-4">
          <Button variant="outline" className="w-full flex items-center justify-between" onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}>
            <span className="flex items-center gap-2">
              <Funnel className="h-4 w-4" />
              Funnel by Industry
            </span>
            <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs font-medium">
              {selectedIndustries.length}
            </span>
          </Button>
        </div>

        <aside className={cn("lg:w-64 shrink-0", isMobileFilterOpen ? "block" : "hidden lg:block")}>
          <div className="sticky top-24 rounded-xl border border-border bg-card p-6 shadow-sm">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Funnel className="h-4 w-4 text-primary" />
              Industries
            </h3>
            <div className="space-y-4">
              {INDUSTRIES.map((industry, i) => <div key={industry.id} data-index={i} className="flex items-center space-x-3">
                  <Checkbox id={`industry-${industry.id}`} checked={selectedIndustries.includes(industry.id)} onCheckedChange={checked => handleIndustryChange(industry.id, checked as boolean)} />
                  <label htmlFor={`industry-${industry.id}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer text-muted-foreground hover:text-foreground transition-colors">
                    {industry.label}
                  </label>
                </div>)}
            </div>
            
            {selectedIndustries.length > 0 && <Button variant="ghost" size="sm" className="w-full mt-6 text-muted-foreground hover:text-foreground" onClick={() => {
            setSelectedIndustries([]);
            setQuery(prev => ({
              ...prev,
              categories: undefined
            }));
          }}>
                Clear Filters
              </Button>}
          </div>
        </aside>

        <div className="flex-1">
          {error && <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
              Failed to load trademarks. Please try again later.
            </div>}

          {loading && !isRefetching ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[280px] rounded-xl bg-muted/50 animate-pulse border border-border" />)}
            </div> : posts.length === 0 ? <div className="text-center py-20 rounded-xl border border-dashed border-border bg-muted/30">
              <ShieldCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium text-foreground mb-2">No trademarks found</h3>
              <p className="text-muted-foreground">Try adjusting your filters to see more results.</p>
            </div> : <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {posts.map((post, i) => {
              const isConfidential = true;
              const trademarkClass = post.customFields?.trademark_class || `Class ${Math.floor(Math.random() * 45) + 1}`;
              return <Card key={post.id} data-index={i} className="border-t-4 border-t-primary overflow-hidden flex flex-col h-full hover:shadow-md transition-shadow duration-300 bg-card">
                      <CardHeader className="pb-4">
                        <div className="flex justify-between items-start mb-3">
                          <span className="inline-flex items-center rounded-full bg-secondary/10 px-2.5 py-0.5 text-xs font-semibold text-secondary">
                            Registered
                          </span>
                          
                        </div>
                        <div className="flex items-center gap-2">
                          {isConfidential && <Lock className="h-4 w-4 text-muted-foreground shrink-0" />}
                          <CardTitle className={cn("text-xl font-bold text-foreground line-clamp-1", isConfidential && "blur-[4px] select-none opacity-70")}>
                            {isConfidential ? "Confidential Brand Name" : post.title}
                          </CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="flex-grow" />
                      <CardFooter className="pt-4 border-t border-border/50 mt-auto">
                        <Link to="/contact" className="w-full">
                          <Button variant="outline" className="w-full group hover:border-primary hover:text-primary transition-colors bg-background">
                            Know More
                            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                          </Button>
                        </Link>
                      </CardFooter>
                    </Card>;
            })}
              </div>

              {hasNext && <div className="mt-12 text-center">
                  <Button onClick={async () => await loadMore()} disabled={isRefetching} size="lg" className="min-w-[200px]">
                    {isRefetching ? "Loading..." : "Load More Trademarks"}
                  </Button>
                </div>}
            </>}

          <div className="mt-20 bg-accent/30 border border-border rounded-2xl p-8 md:p-12 shadow-sm">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-foreground flex items-center gap-3">
                <BookOpen className="h-6 w-6 text-primary" />
                Trademark Classes Reference
              </h3>
            </div>
            
            <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
              <table className="w-full text-sm text-left border-collapse">
                <thead className="bg-muted/50 text-foreground font-semibold border-b border-border">
                  <tr>
                    <th className="px-6 py-4 font-mono text-xs uppercase tracking-wider">Class</th>
                    <th className="px-6 py-4 font-mono text-xs uppercase tracking-wider">Type</th>
                    <th className="px-6 py-4 font-mono text-xs uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {displayedClasses.map((item, i) => <tr key={item.id} data-index={i} className="hover:bg-muted/20 transition-colors">
                      <td className="px-6 py-4 font-mono font-bold text-primary whitespace-nowrap flex items-center gap-2">
                        <Lock className="h-4 w-4" />
                        #{item.classNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase", item.type === "Goods" ? "bg-secondary/10 text-secondary" : "bg-primary/10 text-primary")}>
                          {item.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground leading-relaxed">
                        {item.description}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function TrademarkPortfolio() {
  const wp_query_params = {
    per_page: 9,
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="trademark-portfolio" className="bg-background py-16 md:py-24">
      <PostsProvider wp_query={{
      per_page: 9,
      status: ["publish"]
    }}>
        <PortfolioContent />
      </PostsProvider>
    </section>;
}